import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { AvailableThesaurusTerms } from '@interfaces/available-thesaurus-terms.interface';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { ThesaurusTerm } from '@interfaces/thesaurus-term.interface';
import { ConfigInfo } from '@interfaces/config-info.interface';
import { SearchResults } from '@interfaces/search-results.interface';
import { ThesaurusCategory } from '../../interfaces/thesaurus-category.interface';
import { AvailableThesaurusCategories } from '../../interfaces/available-thesaurus-categories.interface';

const THESAURUS_GATEWAY_URL = `/thesaurus-gateway/thesaurus`;

@Injectable({
  providedIn: 'root'
})
export class ThesaurusService {

  config: ConfigInfo[] = [];
  private cachedThesauri: Thesaurus[] = [];
  private serviceError: boolean = false;
  private serviceErrorSource: string = "";
  private count: number = 50;
  nextPage: boolean = false;
  displayButtons: boolean = false;
  hasSuggestedTerms: boolean = false;

  constructor(
    private http: HttpClient
  ) {};

  private getCachedThesauri(): Observable<Thesaurus[]> {
    return of(this.cachedThesauri);
  }

  getThesauri(token: string, selecteddatabases: string, selectedthesaurus: string): Observable<Thesaurus[]> {
    if (this.cachedThesauri.length > 0) {
      return this.getCachedThesauri();
    }
    var url = `${THESAURUS_GATEWAY_URL}?token=${token}`;
    if(selecteddatabases !== '') {
      url += `&selectedDatabases=${selecteddatabases}`;
    }
    if(selectedthesaurus !== '') {
      url += `&thesaurus=${selectedthesaurus}`;
    }
    return this.http.get<Thesaurus[]>(
      url, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));

            const source: string = "thesauri";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']) {
              let obj = responseObject['thesaurusResponse']['success']['thesauri'];
              this.cachedThesauri = this.sortByName(obj);
              return this.cachedThesauri;
            }
            return [];
          })
      );
  }

  getAvailableThesaurusTerms(thesaurus: Thesaurus, token: string): Observable<AvailableThesaurusTerms> {
    return this.http.get<AvailableThesaurusTerms>(
      `${THESAURUS_GATEWAY_URL}/${thesaurus.id}/term`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            const source: string = "terms";
            this.verifyResponse(response.status, responseObject, source);

            let terms: ThesaurusTerm [] = [];
            if (responseObject['thesaurusResponse']['success']) {
              const availableTerms = responseObject['thesaurusResponse']['success']['availableTerms'];
              terms = this.formatTerms(availableTerms);
            }

            return {
             thesaurus: thesaurus,
             availableTerms: terms
            };
        })
    );
  }

  getAvailableThesaurusCategories(thesaurus: Thesaurus, token: string): Observable<AvailableThesaurusCategories> {
    return this.http.get<AvailableThesaurusCategories>(
      `${THESAURUS_GATEWAY_URL}/${thesaurus.id}/category`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            const source: string = "categories";
            this.verifyResponse(response.status, responseObject, source);

            let categories: ThesaurusCategory[] = [];
            if (responseObject['thesaurusResponse']['success']) {
              const availableCategories = responseObject['thesaurusResponse']['success']['availableTerms'];
              categories = this.formatCategories(availableCategories);
            }

            return {
             thesaurus: thesaurus,
             availableCategories: categories
            };
        })
    );
  }

  //Removes the name of the facet from each string in the array.
  formatTerms(allTerms: string []): ThesaurusTerm [] {
    let formattedTerms: ThesaurusTerm [] = [];

    for (let idx in allTerms) {
      let term = allTerms[idx].replace("text1", "").toUpperCase();

      if (!term.match(/^[0-9A-Z]+$/)) {
        // Filter out non-alpanumeric terms...
        continue;
      }

      if (term === '0') {
        term = '0-9';
      }

      formattedTerms.push({facet: allTerms[idx], display: term, displayUrl: true});
    }
    return formattedTerms;
  }
  
  formatCategories(allCategories: ThesaurusCategory []): ThesaurusCategory [] {
    let formattedCategories: ThesaurusCategory [] = [];

    for (let idx in allCategories) {
      formattedCategories.push({category: allCategories[idx].category, displayName: allCategories[idx].displayName});
    }
    //console.log(formattedCategories);
    return formattedCategories;
  }

  getthesaurusSingleEntry(thesaurusId: string, entryOrdinal: number, token: string): Observable<ThesaurusEntry> {
    return this.http.get<ThesaurusEntry>(
      `${THESAURUS_GATEWAY_URL}/${thesaurusId}/entry/${entryOrdinal}`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));

            const source = "entry";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']) {
              return responseObject['thesaurusResponse']['success'];
            }
            return null;
        }
      )
    );
  }

  getthesaurusRoot(thesaurusId: string, token: string): Observable<ThesaurusEntry> {
    return this.http.get<ThesaurusEntry>(
      `${THESAURUS_GATEWAY_URL}/${thesaurusId}/root`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            console.log(responseObject);

            const source = "entry";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']) {
              return responseObject['thesaurusResponse']['success'];
            }
            return null;
        }
      )
    );
  }

  getThesaurusEntriesByFacet(thesaurus: Thesaurus, selectedFacet: string, token: string, offset: number): Observable<ThesaurusEntry[]> {
    return this.http.get<ThesaurusEntry[]>(
      `${THESAURUS_GATEWAY_URL}/${thesaurus.id}/entry?offset=${offset}&count=${this.count}&facet=${selectedFacet}`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            this.nextPage = false;
            this.displayButtons = false;

            const source = "entries";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']['entries']) {

              //If 'total' is +1 greater than the count, then there is a "next page"
              //If there is a "next page", display the Previous and Next buttons for pagination
              if (responseObject['thesaurusResponse']['success']['total'] > this.count) {
                this.nextPage = true;
                this.displayButtons = true;
              }
              else if (offset > 0) {
                //If there is not a "next page" and the offset is greater than 0, then the user is on the last page of results. Display the Previous button, but not the Next button.
                this.displayButtons = true;
              }
              return responseObject['thesaurusResponse']['success']['entries'];
            }

            return [];
      })
    );
  }

  getThesaurusEntriesSearchTerm(thesaurus: Thesaurus, searchTerm: string, searchOption: string, token: string, offset: number, category?: string, searchType?: string): Observable<SearchResults> {
    let urlFragment = searchOption === "contains" ? "keyword" : "beginsWith";
    let sanitizedCategory =  this.sanitizeForGateway(category);
    let sanitizedType =  this.sanitizeForGateway(searchType);

    return this.http.get<SearchResults>(
      `${THESAURUS_GATEWAY_URL}/${thesaurus.id}/entry?` + urlFragment + `=${searchTerm}&offset=${offset}&count=${this.count}&category=${sanitizedCategory}&type=${sanitizedType}`, {

        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            this.nextPage = false;
            this.displayButtons = false;

            const source = "entries";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']['entries']) {

              //If 'total' is +1 greater than the count, then there is a "next page"
              //If there is a "next page", display the Previous and Next buttons for pagination
              if (responseObject['thesaurusResponse']['success']['total'] > this.count) {
                this.nextPage = true;
                this.displayButtons = true;
              }
              else if (offset > 0) {
                //If there is not a "next page" and the offset is greater than 0, then the user is on the last page of results. Display the Previous button, but not the Next button.
                this.displayButtons = true;
              }
              this.hasSuggestedTerms = responseObject['thesaurusResponse']['success']['hasSuggestedTerms']?? false;
              return {thesaurusEntries: responseObject['thesaurusResponse']['success']['entries'],
                      hasSuggestedTerms: this.hasSuggestedTerms};
            }

            return {thesaurusEntries:[], hasSuggestedTerms: false};
      })
    );
  }

  isDisplayPrevNextButtons() {
    return this.displayButtons;
  }

  isNextPageExists() {
    return this.nextPage;
  }

  getThesaurusEntriesAll(thesaurus: Thesaurus, token: string, offset: number): Observable<ThesaurusEntry[]> {
    return this.http.get<ThesaurusEntry[]>(
      `${THESAURUS_GATEWAY_URL}/${thesaurus.id}/entry?offset=${offset}&count=${this.count}`, {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        observe: "response",
      }).pipe(
          map(response => {
            const responseObject = JSON.parse(JSON.stringify(response.body));
            this.nextPage = false;
            this.displayButtons = false;

            const source = "entries";
            this.verifyResponse(response.status, responseObject, source);

            if (responseObject['thesaurusResponse']['success']['entries']) {

              //If 'total' is +1 greater than the count, then there is a "next page"
              if (responseObject['thesaurusResponse']['success']['total'] > this.count) {
                this.nextPage = true;
                this.displayButtons = true;
              }
              else if (offset > 0) {
                //If there is not a "next page" and the offset is greater than 0, then the user is on the last page of results. Display the Previous button, but not the Next button.
                this.displayButtons = true;
              }
              return responseObject['thesaurusResponse']['success']['entries'];
            }
            return [];
      })
    );
  }

  getThesaurusConfigInformation(): Observable<ConfigInfo> {
    return this.http.get<string>(
      `/thesaurus-gateway/maintain`, {
        headers: new HttpHeaders({
          'Accept': 'application/json'
        })
    }).pipe(
      map(response => {
        const responseObject = JSON.parse(JSON.stringify(response));
        if (responseObject['thesaurusResponse']['success']) {
          return responseObject['thesaurusResponse']['success'];
        }
        // TODO: Handle responseObject['thesaurusResponse']['error']
        return [];
      })
    );
  }

  //The value returned by this function determines if the user will see an error message in the browser window. If this function returns true, the service-error component will be invoked.
  isThesaurusServiceError(): boolean {
    return this.serviceError;
  }

  setThesaurusServiceError(serviceError: boolean) {
    this.serviceError = serviceError;
  }

  getServiceErrorSource(): string {
    return this.serviceErrorSource;
  }

  setServiceErrorSource(message: string) {
    this.serviceErrorSource = message;
  }

  //Verifies the HTTP status of the GET request. If the HTTP response is unsuccessful, if the server response is null, or if the server returns an error message, an error will appear in the browser console.
  //Also sets the value for the source of the error, to prevent duplicate error messages from being presented to the user.
  verifyResponse(status: number, response: any, source: string): void {
    if (this.isStatusError(status) || response == null || response['thesaurusResponse']['error']) {
      this.serviceError = true;
      this.serviceErrorSource = source;
      throw new Error(`Error retrieving ${source}.`);
    }
  }

  //Verifies if the HTTP status code is either a client or server-side error
  isStatusError(status: number): boolean {
    return status > 399;
  }

  //TODO: Review after i18n implementation. Sorting may need to be adapted to account for diacritics, etc.
  sortByName(thesauri: Thesaurus[]): Thesaurus[] {
    thesauri.sort((a, b) => {
      return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
    });
    return thesauri;
  }

  isLexiconThesaurus(thesaurusId: string): boolean {
    return thesaurusId.includes("acta_lexicon_qualifiers");
  }

  isCorporationThesaurus(thesaurusId: string): boolean {
    return thesaurusId.includes("company");
  }

  isBusinessThesaurus(thesaurusId: string): boolean {
    return thesaurusId.includes("pqbuscat");
  }

  isThesaurusWithRoot(thesaurusId: string): boolean {
    return thesaurusId.includes("medline") || thesaurusId.includes("psychinfo");
  }

  //Sanitizes values for the gateway because undefined JS values are misinterpreted as an "undefined" string in Java
  sanitizeForGateway(value: any): string {
    return (value === undefined || value === null) ? "" : value;
  }
}
